/* ==========================================================================
   VSR Cinematic — the hero as a scroll-driven story
   Two editorial movements play out inside a pinned frame while the page scrolls. Scroll
   position IS the timeline, so the reader controls the pace in both
   directions and nothing plays behind their back.
   No dependencies. Degrades to a plain stacked page under reduced motion.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var root = document.documentElement;

  /* ---------- helpers ---------- */
  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };
  // progress of x through [a,b], clamped to 0..1
  var band = function (x, a, b) { return clamp((x - a) / (b - a), 0, 1); };
  // 0 at the edges of [a,b], 1 across the middle — an act fading in and out
  var window_ = function (x, a, b, fade) {
    fade = fade || 0.18;
    return Math.min(band(x, a, a + (b - a) * fade),
                    1 - band(x, b - (b - a) * fade, b));
  };
  var ease = function (t) { return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };

  /* ---------- split text into word spans ---------- */
  function split(el) {
    if (!el || el.dataset.vsrSplit) return;
    var html = el.innerHTML;
    // keep inline tags intact by splitting only bare text nodes
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var nodes = [], n;
    while ((n = walker.nextNode())) nodes.push(n);
    nodes.forEach(function (node) {
      if (!node.nodeValue.trim()) return;
      var frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
        var s = document.createElement('span');
        s.className = 'w';
        s.textContent = part;
        frag.appendChild(s);
      });
      node.parentNode.replaceChild(frag, node);
    });
    el.dataset.vsrSplit = '1';
    el.__words = el.querySelectorAll('.w');
    el.__html = html;
  }

  /* ---------- one shared scroll loop ---------- */
  var subs = [], ticking = false;
  function tick() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var y = window.pageYOffset || root.scrollTop;
      for (var i = 0; i < subs.length; i++) {
        try { subs[i](y); } catch (e) {}
      }
      ticking = false;
    });
  }
  function onScroll(fn) { subs.push(fn); fn(window.pageYOffset || root.scrollTop); }

  /* ---------- the stage ---------- */
  function initStage() {
    var stage = document.querySelector('.vsr-stage');
    if (!stage) return;
    var frame = stage.querySelector('.vsr-stage-frame');
    var media = stage.querySelector('.vsr-stage-media');
    var acts = [].slice.call(stage.querySelectorAll('.vsr-act'));
    var nav = stage.querySelector('.vsr-acts-nav');
    var cue = stage.querySelector('.vsr-cue');
    var disc = stage.querySelector('.vsr-record');
    if (!acts.length) return;

    // Keep the moving hero, but do no decoding once it leaves the viewport or
    // the tab is hidden. The source is intentionally cinematic and large;
    // this prevents it taxing every page section after the opening scene.
    if (media) {
      var mediaVisible = true;
      media.muted = true;
      media.playsInline = true;
      media.style.filter = 'brightness(.48) saturate(1.24)';
      var syncPlayback = function () {
        if (document.hidden || !mediaVisible || matchMedia('(prefers-reduced-motion: reduce)').matches) {
          media.pause();
          return;
        }
        var promise = media.play();
        if (promise && promise.catch) promise.catch(function () {});
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          mediaVisible = !!entries[0] && entries[0].isIntersecting;
          syncPlayback();
        }, { rootMargin: '160px 0px', threshold: 0.01 }).observe(stage);
      }
      document.addEventListener('visibilitychange', syncPlayback);
      matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', syncPlayback);
      window.addEventListener('pageshow', syncPlayback);
      syncPlayback();
    }

    acts.forEach(function (a) {
      var t = a.querySelector('.vsr-wordmark, .vsr-thesis');
      if (t) split(t);
    });

    // one nav tick per act
    if (nav && !nav.children.length) {
      acts.forEach(function () { nav.appendChild(document.createElement('i')); });
    }
    var ticks = nav ? [].slice.call(nav.children) : [];

    if (reduced) {
      acts.forEach(function (a) { a.style.opacity = 1; a.classList.add('is-live'); });
      return;
    }

    onScroll(function (y) {
      // Phones use one composed opening, not two pinned screens. Keep the
      // record's scroll rotation, but skip per-word/filter work on every scroll.
      if (matchMedia('(max-width: 749px)').matches) {
        acts.forEach(function(act, index) { act.inert = index !== 0; });
        if (disc) disc.style.transform = 'rotate(' + (y * 0.16).toFixed(2) + 'deg)';
        return;
      }
      var top = stage.offsetTop;
      var h = stage.offsetHeight - window.innerHeight;
      var p = clamp((y - top) / Math.max(h, 1), 0, 1);   // 0..1 across the stage

      // backdrop: slow push-in the whole way through, brightening slightly
      if (media) {
        // Backdrop only pushes in. Rotating the whole frame turned the entire
        // video, which is not what a turning record looks like — the disc
        // element below does the spinning.
        media.style.transform = 'scale(' + (1 + p * 0.16).toFixed(4) + ')';
      }

      // the record itself spins, tied to scroll position
      if (disc) disc.style.transform = 'rotate(' + (y * 0.16).toFixed(2) + 'deg)';

      var n = acts.length;
      for (var i = 0; i < n; i++) {
        var a = i / n, b = (i + 1) / n;
        // Act 1 is the landing state: fully composed before a single pixel of
        // scroll, so it only ever fades OUT. Every later act fades in and out.
        var o = (i === 0)
          ? 1 - band(p, b - (b - a) * 0.22, b)
          : (i === n - 1 ? band(p, a, a + (b - a) * 0.22) : window_(p, a, b, 0.22));
        var act = acts[i];
        act.style.opacity = o.toFixed(3);
        act.classList.toggle('is-live', o > 0.5);
        act.inert = o <= 0.5;

        // local progress inside this act drives its own motion
        var lp = band(p, a, b);
        act.style.transform = 'translate3d(0,' + ((0.5 - lp) * 46).toFixed(1) + 'px,0)';

        // word-level reveal, staggered by index
        var words = act.__wordsCache ||
          (act.__wordsCache = act.querySelectorAll('.vsr-wordmark .w, .vsr-thesis .w'));
        if (words.length) {
          for (var w = 0; w < words.length; w++) {
            // Act 1's words are already settled at rest; they animate on page
            // load instead (see the CSS entrance), so scroll only moves them.
            // Resolve the words early in the act (stagger across the first
            // ~12%, each settling in 18%) so the line is READABLE for the bulk
            // of the act. A stagger spread over 66% leaves most of the act
            // showing a near-empty screen.
            var wp = (i === 0)
              ? 1
              : clamp((lp - (w / words.length) * 0.12) / 0.18, 0, 1);
            var e = ease(wp);
            words[w].style.opacity = e.toFixed(3);
            words[w].style.transform =
              'translate3d(0,' + ((1 - e) * 34).toFixed(1) + 'px,0) ' +
              'scale(' + (0.94 + e * 0.06).toFixed(3) + ')';
            words[w].style.filter = 'blur(' + ((1 - e) * 3.5).toFixed(1) + 'px)';
          }
        }

        if (ticks[i]) ticks[i].style.setProperty('--p', band(p, a, b).toFixed(3));
      }

      if (cue) cue.style.opacity = (1 - band(p, 0, 0.12)).toFixed(3);
    });
  }

  /* ---------- animated film grain (canvas beats a tiled png) ---------- */
  function initGrain() {
    var cv = document.querySelector('.vsr-stage-grain');
    if (!cv || reduced) return;
    var ctx = cv.getContext('2d', { alpha: true });
    var W = 160, Hh = 160;
    cv.width = W; cv.height = Hh;
    var frames = [], i;
    for (i = 0; i < 4; i++) {
      var img = ctx.createImageData(W, Hh);
      for (var j = 0; j < img.data.length; j += 4) {
        var v = (Math.random() * 255) | 0;
        img.data[j] = img.data[j + 1] = img.data[j + 2] = v;
        img.data[j + 3] = 255;
      }
      frames.push(img);
    }
    // Painted once. Re-drawing noise every frame kept the compositor busy
    // for a texture nobody perceives as animated.
    ctx.putImageData(frames[0], 0, 0);
  }



  /* ---------- page-wide entrances ---------- */
  function initReveals() {
    var targets = document.querySelectorAll(
      '.vsr-rise, .vsr-rule, .vsr-split, ' +
      '.shopify-section h2, .shopify-section .card-wrapper, ' +
      '.vsr-portrait-card, .vsr-roster-grid .vsr-artist-card'
    );
    if (!targets.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (t) { t.classList.add('is-in', 'vsr-in'); });
      return;
    }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        var el = e.target;
        if (el.classList.contains('vsr-split')) split(el);
        var sibs = el.parentElement
          ? Array.prototype.indexOf.call(el.parentElement.children, el) : 0;
        el.style.transitionDelay = Math.min(sibs * 55, 400) + 'ms';
        el.classList.add('is-in', 'vsr-in');
        // stagger the words inside a split heading
        var ws = el.querySelectorAll('.w');
        for (var k = 0; k < ws.length; k++) ws[k].style.transitionDelay = (k * 45) + 'ms';
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    Array.prototype.forEach.call(targets, function (t) {
      if (!t.classList.contains('vsr-rise') && !t.classList.contains('vsr-rule') &&
          !t.classList.contains('vsr-split')) t.classList.add('vsr-rise');
      io.observe(t);
    });
  }

  /* ---------- magnetic buttons ---------- */
  function initMagnets() {
    if (reduced || matchMedia('(hover: none)').matches) return;
    var els = document.querySelectorAll('.vsr-door, .btn, .button');
    Array.prototype.forEach.call(els, function (el) {
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) / r.width;
        var dy = (e.clientY - (r.top + r.height / 2)) / r.height;
        el.style.transform = 'translate(' + (dx * 9).toFixed(1) + 'px,' + (dy * 6).toFixed(1) + 'px)';
      }, { passive: true });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; }, { passive: true });
    });
  }

  function boot() {
    initStage();
    initGrain();
    initReveals();
    initMagnets();
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick, { passive: true });
    root.classList.add('vsr-cinematic-on');
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
